import '../styles/pdf-pages.css';
import '../styles/model-overview.css';

import { selectIsAuthenticated } from '@/domain/auth/auth.selectors';
import { CalculateTourCo2 } from '@/domain/impact-calculate-tour/entities/calculate-tour-co2.entity';
import {
  selectImpactCalculateTourCo2ForProject,
  selectImpactCalculateTourForProject,
} from '@/domain/impact-calculate-tour/impact-calculate-tour.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { selectCurrentProjectTour } from '@/domain/project-tour/project-tour.selectors';
import { selectProjectTourOnTourSequences } from '@/domain/project-tour-ontour/project-tour-ontour.selectors';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { formatProjectDatesTz } from '@/ui/utils/format-project-dates';

import CalendarMonthSvg from '../assets/calendar_month.svg';
import { PdfProjectPager } from '../components/PdfProjectPager';
import { PdfScoreCard } from '../components/PdfScoreCard';
import { TourOnTourSequenceCard } from '../components/TourOnTourSequenceCard';
import styles from '../styles/TourOnTourSequences.module.css';
import { displayDecimal } from '../utils/display-scores.utils';

export function PdfTourPage3({ totalPages }: { totalPages: number }) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const tour = useAppSelector(
    selectCurrentProjectTour,
  ) satisfies ProjectTour | null;
  const tourId = tour?.id ?? 0;

  const impactCo2 = useAppSelector(
    selectImpactCalculateTourCo2ForProject(tourId),
  ) satisfies CalculateTourCo2;

  const calculateTour = useAppSelector(
    selectImpactCalculateTourForProject(tourId),
  );

  const sequences = useAppSelector(selectProjectTourOnTourSequences);

  if (!isAuthenticated) {
    return <h1>{translate('PdfExports.user-not-authentified')}</h1>;
  }

  if (!tour) {
    return <h1>{translate('PdfExports.tour-not-found')}</h1>;
  }

  const ontour = impactCo2.ontour;
  const statsBySequence = calculateTour?.stats?.ontour.bySequence ?? {};

  return (
    <div id="page3" className="pdf-page">
      <div className="content">
        <div className="pdf-light-header">
          <div>
            <h1>{tour.name}</h1>
            {tour.startDate && tour.endDate && (
              <h2>
                <img
                  src={CalendarMonthSvg}
                  width="19"
                  height="19"
                  style={{ position: 'relative', top: 4, marginRight: 5 }}
                  alt="Calendar icon"
                />
                <span className="tour-date">
                  {formatProjectDatesTz(tour.startDate, tour.endDate)}
                </span>
              </h2>
            )}
          </div>
        </div>
        <div className="pdf-separator"></div>

        <div className="model-header">
          <div>
            <h2>{translate('PdfExports.co2-score-details')}</h2>
            <h5>{translate('ProjectsTour.project-tour-ontour')}</h5>
          </div>
          <div>
            <h4>
              <span className="highlight">{displayDecimal(ontour.score)} </span>
              {translate('Projects.teCO2')}
            </h4>
          </div>
          <div>
            <PdfScoreCard type="co2" scoreLetter="-" size="medium" />
          </div>
        </div>

        <div className={styles.sequences}>
          {sequences.map((sequence) => {
            const sequenceCo2 = ontour.bySequence[sequence.id];
            const sequenceStats = statsBySequence[sequence.id];
            const variousCostCategories =
              sequenceCo2?.variousCost.sectors.artists.categories;

            return (
              <TourOnTourSequenceCard
                key={sequence.id}
                name={sequence.name}
                eventCount={sequenceStats?.eventCount ?? 0}
                totalCo2={sequenceCo2?.score ?? 0}
                co2PerDate={sequenceStats?.co2AveragePerEvent ?? 0}
                datesImpact={sequenceCo2?.events ?? 0}
                travelsImpact={sequenceCo2?.travels ?? 0}
                accommodationImpact={
                  variousCostCategories?.accommodation.score ?? 0
                }
                cateringImpact={variousCostCategories?.catering.score ?? 0}
                equipmentImpact={variousCostCategories?.equipment.score ?? 0}
              />
            );
          })}
        </div>

        <div className="navigation-controls">
          <a href="#page2" className="tag-pill">
            {translate('PdfExports.go-back-to-co2-general')}
          </a>
          <a href="#page4" className="tag-pill">
            {translate('PdfExports.go-to-env')}
          </a>
        </div>
      </div>

      <PdfProjectPager current={3} total={totalPages}></PdfProjectPager>
    </div>
  );
}
